// Vendor
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Components
import Button, { ButtonVariant } from 'src/components/atoms/Button';
import { Icon } from 'src/components/atoms/Icon';
import InputSearch from 'src/components/organisms/InputSearch';
import { CompanyMultiSelector } from 'src/pages/ReportingPage/components/CompanyMultiSelector';
import { PropertyStatusFilter } from './PropertyStatusFilter';
import {
  StyledCompanyFilter,
  StyledDiv,
  StyledRow,
  StyledSearch,
  StyledStatusFilter
} from './styles';

// Hooks
import useRole from 'src/features/auth/hooks/useUserRoles';
import useProperty from 'src/features/property/hooks/useProperty';

// Redux
import companyApi from 'src/features/company/api';
import { selectors as companySelectors } from 'src/features/company/companySlice';
import { selectors as propertySelectors } from 'src/features/property/propertySlice';
import { getAll as getAllProperties } from 'src/features/property/services';
import { useAppDispatch } from 'src/store';

// Helpers
import {
  buildPropertyFilter,
  isPropertyFilterNarrowed
} from 'src/features/property/utils/buildPropertyFilter';
import {
  DEFAULT_ROWS_PER_PAGE,
  readPropertyListParams,
  writePropertyListParams
} from 'src/features/property/utils/propertyListParams';

// Enums
import { PropertyStatus } from 'src/ts/enums';

// Types
import { CheckboxType } from 'src/components/atoms/Checkbox/types';

type PropertyFilters = {
  companyIds: string;
  statuses: PropertyStatus[];
};

export interface PropertyListFilterProps {
  identityVerificationPropertySearch?: boolean;
}
const PropertyListFilters: React.FC<PropertyListFilterProps> = (props) => {
  const navigate = useNavigate();

  const { identityVerificationPropertySearch } = props;

  const { onSearch, onSearchIdentityVerificationEnabled, onGetAll, allowCreateProperties } =
    useProperty();

  const { isAdminOrAccountRepresentative } = useRole();

  const [filters, setFilters] = useState<PropertyFilters>({ companyIds: '', statuses: [] });

  // The company dropdown keeps its own selection, so it is rebuilt from each URL
  // read rather than from `filters`, which also changes on the user's own picks.
  const [linkedCompanies, setLinkedCompanies] = useState({ ids: '', read: 0 });
  const [linkedCompanyNames, setLinkedCompanyNames] = useState<Record<string, string>>({});

  // CompanyMultiSelector keeps its first-render onCallback, so read filters
  // through a ref or a company pick wipes the status picks.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const methods = useForm({ defaultValues: { company: '' } });

  const showFilters = !identityVerificationPropertySearch && isAdminOrAccountRepresentative;

  const getPropertiesFilter = useSelector(propertySelectors.getAll.filter);
  const getPropertiesIsLoading = useSelector(propertySelectors.getAll.isLoading);
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const loadedCompanies = useSelector(companySelectors.getCompaniesDDown).data;

  // Ids proved gone, so a second failure cannot resurrect one the first dropped.
  const droppedCompanyIds = useRef<Set<string>>(new Set());
  const lastWrittenParams = useRef('');
  const hasReadUrl = useRef(false);
  // The slice filter a dispatched fetch will replace; a new object means it landed.
  const filterAwaitingFetch = useRef<unknown>(null);

  // URL -> page, skipping the echo of our own writes.
  useEffect(() => {
    if (identityVerificationPropertySearch) return;

    const incoming = searchParams.toString();
    if (hasReadUrl.current && incoming === lastWrittenParams.current) return;
    hasReadUrl.current = true;

    const urlState = readPropertyListParams(searchParams, {
      allowFilters: Boolean(showFilters)
    });

    setFilters({ companyIds: urlState.companyIds, statuses: urlState.statuses });
    setLinkedCompanies((current) => ({ ids: urlState.companyIds, read: current.read + 1 }));

    urlState.companyIds
      .split(',')
      .filter(Boolean)
      .forEach((id) => {
        companyApi
          .getCompany(id)
          .then(({ data }) => {
            if (data?.name) setLinkedCompanyNames((names) => ({ ...names, [id]: data.name }));
          })
          .catch((error) => {
            // A company we simply could not reach must keep filtering. Only one
            // that is actually gone gets dropped.
            if (error?.response?.status !== 404) return;

            droppedCompanyIds.current.add(id);

            const keep = (ids: string) =>
              ids
                .split(',')
                .filter((linkedId) => linkedId && !droppedCompanyIds.current.has(linkedId))
                .join(',');

            const remaining = keep(urlState.companyIds);

            setLinkedCompanies((current) => ({ ...current, ids: keep(current.ids) }));
            setFilters((current) => ({ ...current, companyIds: keep(current.companyIds) }));

            // The request already in flight still names the dead company, so ask
            // again without it rather than showing an empty list.
            dispatch(
              getAllProperties({
                ...getPropertiesFilter,
                q: urlState.q ? encodeURIComponent(urlState.q) : '',
                page: urlState.page,
                rowsPerPage: urlState.rowsPerPage,
                filter: buildPropertyFilter({
                  companyIds: remaining,
                  statuses: urlState.statuses
                })
              })
            );
          });
      });

    const request = {
      ...getPropertiesFilter,
      q: urlState.q ? encodeURIComponent(urlState.q) : '',
      page: urlState.page,
      rowsPerPage: urlState.rowsPerPage,
      filter: buildPropertyFilter({
        companyIds: urlState.companyIds,
        statuses: urlState.statuses
      })
    };

    // `status!=ARCHIVED` and `status@>ACTIVE|DISABLED` match the same rows.
    const sameFilter =
      request.filter === getPropertiesFilter.filter ||
      (!isPropertyFilterNarrowed(request.filter) &&
        !isPropertyFilterNarrowed(getPropertiesFilter.filter));

    const alreadyShowing =
      sameFilter &&
      request.q === (getPropertiesFilter.q || '') &&
      request.page === getPropertiesFilter.page &&
      request.rowsPerPage === getPropertiesFilter.rowsPerPage;

    if (!alreadyShowing) {
      // Claim the incoming URL so the write effect does not replace it with the
      // Redux state this fetch is about to change.
      lastWrittenParams.current = incoming;
      filterAwaitingFetch.current = getPropertiesFilter;
      dispatch(getAllProperties(request));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, showFilters]);

  // Page -> URL, only after the first read so defaults never overwrite a link.
  useEffect(() => {
    // Mid-fetch, `filters` is new while the Redux page and search are not.
    if (identityVerificationPropertySearch || !hasReadUrl.current || getPropertiesIsLoading) {
      return;
    }

    if (filterAwaitingFetch.current !== null) {
      if (filterAwaitingFetch.current === getPropertiesFilter) return;
      filterAwaitingFetch.current = null;
    }

    const next = writePropertyListParams({
      companyIds: filters.companyIds,
      statuses: filters.statuses,
      q: getPropertiesFilter.q ? decodeURIComponent(getPropertiesFilter.q) : '',
      page: getPropertiesFilter.page ?? 0,
      rowsPerPage: getPropertiesFilter.rowsPerPage ?? DEFAULT_ROWS_PER_PAGE
    });
    const nextParams = new URLSearchParams(next).toString();

    if (nextParams === searchParams.toString()) return;

    lastWrittenParams.current = nextParams;
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, getPropertiesFilter, getPropertiesIsLoading]);

  // Wait until every linked company has a name, so a chip never shows a raw id.
  const preselectedCompanies = useMemo(() => {
    if (!linkedCompanies.ids) return undefined;

    const ids = linkedCompanies.ids.split(',');
    const named = ids
      .map((id) => {
        const name =
          loadedCompanies?.find((company) => company.id === id)?.name || linkedCompanyNames[id];
        return name ? { value: id, label: name, isChecked: true } : null;
      })
      .filter((item): item is CheckboxType => item !== null);

    return named.length === ids.length ? named : undefined;
  }, [linkedCompanies.ids, loadedCompanies, linkedCompanyNames]);

  const getPropertiesError = useSelector(propertySelectors.getAll.error);
  const getPropertiesIsToolbarDisabled = useSelector(propertySelectors.getAll.isToolbarDisabled);

  const getIdentityVerificationEnabledPropertiesFilter = useSelector(
    propertySelectors.getAllIdentityVerificationEnabled.filter
  );
  const getIdentityVerificationEnabledPropertiesIsToolbarDisabled = useSelector(
    propertySelectors.getAllIdentityVerificationEnabled.isToolbarDisabled
  );

  const applyFilters = (patch: Partial<PropertyFilters>) => {
    const next = { ...filtersRef.current, ...patch };
    setFilters(next);
    onGetAll(0, undefined, buildPropertyFilter(next));
  };

  return (
    <StyledRow gap={1} justify="flex-end" wrap="wrap">
      {!identityVerificationPropertySearch && (
        <StyledSearch>
          <InputSearch
            onSearch={({ value }) => onSearch(value)}
            onClear={() => onSearch('')}
            name="PropertyListSearch"
            value={(getPropertiesFilter.q && decodeURIComponent(getPropertiesFilter?.q)) || ''}
            placeholder="Search"
            isDisabled={
              getPropertiesIsLoading ||
              getPropertiesError !== null ||
              (getPropertiesIsToolbarDisabled &&
                !isPropertyFilterNarrowed(getPropertiesFilter.filter))
            }
          />
        </StyledSearch>
      )}
      {identityVerificationPropertySearch && (
        <InputSearch
          onSearch={({ value }) => onSearchIdentityVerificationEnabled(value)}
          onClear={() => onSearchIdentityVerificationEnabled('')}
          name="IdentityVerificationEnabledPropertyListSearch"
          value={
            (getIdentityVerificationEnabledPropertiesFilter.q &&
              decodeURIComponent(getIdentityVerificationEnabledPropertiesFilter?.q)) ||
            ''
          }
          placeholder="Search"
          isDisabled={getIdentityVerificationEnabledPropertiesIsToolbarDisabled}
        />
      )}

      {showFilters && (
        <StyledStatusFilter>
          <PropertyStatusFilter
            selected={filters.statuses}
            isDisabled={getPropertiesIsLoading || getPropertiesError !== null}
            onChange={(statuses) => applyFilters({ statuses })}
          />
        </StyledStatusFilter>
      )}

      {!identityVerificationPropertySearch && allowCreateProperties() && (
        <StyledDiv>
          <Button
            name="add_property_button"
            variant={ButtonVariant.contained}
            onClick={() => navigate(`/properties/create`)}
            isDisabled={getPropertiesIsLoading || getPropertiesError !== null}
          >
            Add property&nbsp;&nbsp;
            <Icon icon="plus" />
          </Button>
        </StyledDiv>
      )}

      {showFilters && (
        <StyledCompanyFilter>
          <FormProvider {...methods}>
            <CompanyMultiSelector
              key={linkedCompanies.read}
              methods={methods}
              valueKey="id"
              preselected={preselectedCompanies}
              onCallback={({ ids, isCheckAll }) => {
                const companyIds = isCheckAll ? '' : ids;
                methods.setValue('company', companyIds);
                applyFilters({ companyIds });
              }}
            />
          </FormProvider>
        </StyledCompanyFilter>
      )}
    </StyledRow>
  );
};

PropertyListFilters.propTypes = {
  identityVerificationPropertySearch: PropTypes.bool
};
export default PropertyListFilters;
